/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Table,
  Pagination,
  Modal,
  Tag,
  Input,
  Space,
  Descriptions,
  Form,
  Grid,
  Select
} from "antd";
import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";

import CreateSubscriptionModal from "./CreateModal";
import EditSubscriptionConfigModal from "./EditConfigModal";
import { subscriptionTypesList } from "./helper";
import LinkPaymentModal from "./LinkPaymentModal";
import UserProgressModal from "./UserProgressModal";
import {
  showErrorNotification,
  showNotification,
  showSuccessNotification
} from "../../../utils/notification";

const { Search } = Input;

const CourseUsersModal = ({
  courseId,
  displayName = "displayName",
  isModalOpen = false,
  setIsModalOpen = () => {},
  ROOT_URL
}) => {
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [courseSubscriptionStats, setCourseSubscriptionStats] = useState({});
  const [subscribersList, setSubscribersList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isNewSubscriptionModalOpen, setIsNewSubscriptionModalOpen] = useState(false);
  const [isLinkPaymentModalOpen, setIsLinkPaymentModalOpen] = useState(false);
  const [isEditSubscriptionConfigModalOpen, setIsEditSubscriptionConfigModalOpen] = useState(false);
  const [isViewProgressModalOpen, setIsViewProgressModalOpen] = useState(false);
  const [manualCreationAllowed, setManualCreationAllowed] = useState(false);
  const [currentSubscriptionRow, setCurrentSubscriptionRow] = useState();
  const [userProgressId, setUserProgressId] = useState();
  const [subscribersCSVData, setSubscribersCSVData] = useState([]);
  const [adminsNamesList, setAdminsNamesList] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingSubscription, setDeletingSubscription] = useState(null);
  const csvLinkRef = useRef(null);
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const fetchSubscribersList = async (page) => {
    axios
      .get(`${ROOT_URL}/admin/courses/subscriptions/list`, {
        params: {
          courseId,
          page
        }
      })
      .then((res) => {
        const {
          subscriptions = [],
          subscriptionsCount = 0,
          subscriptionsCountStats,
          manualCreationAllowed = false
        } = res.data;
        setCourseSubscriptionStats(subscriptionsCountStats);
        setSubscribersList(subscriptions);
        setTotalSubscriptions(subscriptionsCount);
        setManualCreationAllowed(manualCreationAllowed);

        showSuccessNotification("Subscriptions Fetched Successfully");
      })
      .catch(() => {
        showNotification("Internal Server Error", "Internal Server Error", "error");
      });
  };

  const handleSubscriptionDeletion = (subscription, shouldRefund = false) => {
    axios
      .post(`${ROOT_URL}/admin/courses/subscriptions/delete`, {
        subscriptionId: subscription.key,
        shouldRefund
      })
      .then(() => {
        showSuccessNotification(
          shouldRefund
            ? "Subscription deleted & payment marked as refunded successfully"
            : "Subscription deleted successfully"
        );
        fetchSubscribersList(currentPage);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchSubscribersList(page);
  };

  const handleSubscriptionsSearch = (query) => {
    axios
      .get(`${ROOT_URL}/admin/courses/subscriptions/search`, {
        params: {
          query,
          courseId
        }
      })
      .then((res) => {
        const { subscriptions, subscriptionsCount } = res.data;
        setSubscribersList(subscriptions);
        setTotalSubscriptions(subscriptionsCount);
        showSuccessNotification("Subscribers Fetched Successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleSubscriptionCreate = (params) => {
    axios
      .post(`${ROOT_URL}/admin/courses/subscriptions/create`, {
        courseId,
        ...params
      })
      .then(() => {
        setIsNewSubscriptionModalOpen(false);
        showSuccessNotification("Subscription created Successfully");
        fetchSubscribersList(currentPage);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleUpdateSubscriptionConfig = (updatedConfig) => {
    axios
      .post(`${ROOT_URL}/admin/courses/subscriptions/update-config`, {
        subscriptionId: currentSubscriptionRow.key,
        updatedConfig
      })
      .then(() => {
        showSuccessNotification("Config updated Successfully");
        fetchSubscribersList(currentPage);
        setIsEditSubscriptionConfigModalOpen(false);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleLinkPayment = (params) => {
    axios
      .post(`${ROOT_URL}/admin/courses/subscriptions/link-payment`, {
        userEmail: currentSubscriptionRow.email,
        courseId,
        type: currentSubscriptionRow.type,
        ...params
      })
      .then(() => {
        showSuccessNotification("Payment linked Successfully");
        fetchSubscribersList(currentPage);
        setIsLinkPaymentModalOpen(false);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleDownloadCsv = () => {
    axios
      .get(`${ROOT_URL}/admin/courses/subscriptions/subscribers-csv-data`, {
        params: {
          courseId
        }
      })
      .then((res) => {
        setSubscribersCSVData(
          (res?.data || []).map((item) => ({
            Name: item.name,
            Email: item.email,
            "CodeForces Handle": item.cfHandle,
            "College Name": item.collegeName,
            "Is Discord Connected": item.discordConnected,
            Type: item.type,
            "Reason of Addition": item.reasonOfAddition,
            "Total Scorable Problems": item.totalScorableProblems,
            "Solved Scorable Problems": item.scorableSolvedCount,
            "Total Non Scorable Problems": item.totalNonScorableProblems,
            "Solved Unscorable Problems": item.nonScorableSolvedCount,
            "Doubts Asked": item.doubtsAsked,
            "Discord Expiry Date": item.discordExpiryDate
          }))
        );
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchAdminsNamesList = async () => {
    axios
      .get(`${ROOT_URL}/admin/courses/subscriptions/fetch-admin-names`)
      .then(({ data }) => {
        const { adminsNamesList = [] } = data;
        setAdminsNamesList(adminsNamesList);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleSubmitFilters = async (values) => {
    axios
      .get(`${ROOT_URL}/admin/courses/subscriptions/filter`, {
        params: {
          courseId,
          adminId: values.adminId,
          type: values.type,
          isPaymentLinked: values.isPaymentLinked
        }
      })
      .then(({ data }) => {
        const { subscriptions = [], subscriptionsCount = 0 } = data;
        setSubscribersList(subscriptions);
        setTotalSubscriptions(subscriptionsCount);
        showSuccessNotification("Subscribers Fetched Successfully");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index"
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "CF Handle",
      dataIndex: "cfHandle",
      key: "cfHandle",
      sorter: (a, b) => {
        if (typeof a.cfHandle === "string" && typeof b.cfHandle === "string") {
          return a.cfHandle?.localeCompare(b.cfHandle) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Discord Connected",
      dataIndex: "discordConnected",
      key: "discordConnected",
      sorter: (a, b) => {
        if (typeof a.discordConnected === "string" && typeof b.discordConnected === "string") {
          return a.discordConnected?.localeCompare(b.discordConnected) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Discord Expiry Date",
      dataIndex: "discordExpiryDate",
      key: "discordExpiryDate"
    },
    {
      title: "Type",
      key: "type",
      render: (row) => {
        let statusObj = {
          color: row?.internallyCreatedBy?.length ? "red" : "green",
          text: row?.type
        };
        return (
          <div>
            <Tag color={statusObj.color} key={statusObj.text}>
              {statusObj.text?.toUpperCase()}
            </Tag>
          </div>
        );
      }
    },
    {
      title: "Added By",
      dataIndex: "internallyCreatedBy",
      key: "internallyCreatedBy"
    },
    {
      title: "Status",
      key: "status",
      render: (row) => {
        let statusObj = {
          color: row.isActive ? "green" : "red",
          text: row.isActive ? "ACTIVE" : "DEACTIVATED"
        };
        if (row.archived) {
          statusObj.color = "default";
          statusObj.text = "ARCHIVED";
        }
        return (
          <Tag color={statusObj.color} key={statusObj.text}>
            {statusObj.text.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: "Actions",
      key: "action",
      width: 340,
      fixed: isMobileView ? undefined : "right",
      render: (row) => (
        <div id="course_subs_row_actions">
          <Space wrap>
            <Button
              type="primary"
              disabled={row.archived}
              onClick={() => {
                setUserProgressId(row.userId);
                setIsViewProgressModalOpen(true);
              }}
            >
              View Progress
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setCurrentSubscriptionRow(row);
                setIsEditSubscriptionConfigModalOpen(true);
              }}
            >
              Edit Config
            </Button>
            {row.type === "PURCHASE" && row.isPaymentLinked === false && (
              <Button
                type="primary"
                onClick={() => {
                  setCurrentSubscriptionRow(row);
                  setIsLinkPaymentModalOpen(true);
                }}
              >
                Link Payment
              </Button>
            )}
            <Button
              type="primary"
              disabled={row.archived}
              danger={row.isActive}
              onClick={() => {
                if (row.type === "PURCHASE" && row.isPaymentLinked) {
                  setDeletingSubscription(row);
                  setIsDeleteModalOpen(true);
                } else {
                  handleSubscriptionDeletion(row, false);
                }
              }}
            >
              Delete
            </Button>
          </Space>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchSubscribersList(1);
    fetchAdminsNamesList();
  }, []);

  useEffect(() => {
    if (subscribersCSVData.length > 0 && csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  }, [subscribersCSVData]);

  return (
    <Modal
      title="Subscriptions"
      open={isModalOpen}
      footer={[]}
      width={2500}
      onOk={handleOk}
      onCancel={handleCancel}
    >
      <Descriptions title="Course Subscription Stats" column={{ xs: 1, sm: 2, md: 3 }} bordered>
        <Descriptions.Item label="Total">{courseSubscriptionStats?.total || 0}</Descriptions.Item>
        <Descriptions.Item label="Normal Purchases">
          {courseSubscriptionStats?.websitePurchaseCount || 0}
        </Descriptions.Item>
        <Descriptions.Item label="Admin Additions">
          {courseSubscriptionStats?.addedByAdminCount || 0}
        </Descriptions.Item>
        <Descriptions.Item label="Extensions">
          {courseSubscriptionStats?.extensionsCount || 0}
        </Descriptions.Item>
        <Descriptions.Item label="Internal">
          {courseSubscriptionStats?.internalCount || 0}
        </Descriptions.Item>
        <Descriptions.Item label="Deactivated">
          {courseSubscriptionStats?.deactivatedCount || 0}
        </Descriptions.Item>
        <Descriptions.Item label="Archived">
          {courseSubscriptionStats?.archivedCount || 0}
        </Descriptions.Item>
        <Descriptions.Item label="Remaining Discord Connections">
          {courseSubscriptionStats?.remainingDiscordConnections || 0}
        </Descriptions.Item>
        <Descriptions.Item label="Remaining CF Connections">
          {courseSubscriptionStats?.remainingCodeforcesConnections || 0}
        </Descriptions.Item>
        <Descriptions.Item label="Unlinked Payment Subscriptions">
          {courseSubscriptionStats?.unlinkedPaymentSubscriptions || 0}
        </Descriptions.Item>
      </Descriptions>
      <br />
      <Space wrap style={{ width: "100%" }}>
        <Button
          type="primary"
          disabled={!manualCreationAllowed}
          onClick={() => {
            setIsNewSubscriptionModalOpen(true);
          }}
        >
          Create Subscription
        </Button>
        <CSVLink
          asyncOnClick={true}
          data={subscribersCSVData || []}
          ref={csvLinkRef}
          filename={`${displayName}_Subscription.csv`}
        />
        <Button
          type="primary"
          onClick={() => {
            handleDownloadCsv();
          }}
        >
          Download CSV
        </Button>
        <Search
          placeholder="Search by name or email"
          allowClear
          enterButton="Search"
          onSearch={handleSubscriptionsSearch}
          style={{ width: "min(360px, 100%)" }}
        />
      </Space>
      <br />
      <div>
        <div style={{ width: "100%" }}>
          <Form
            style={{
              display: "flex",
              flexWrap: "wrap",
              // Row gap stays 0 because each Form.Item already carries a 24px bottom margin
              gap: "0 24px",
              // Form.Item's margin only pads below, so the toolbar above needs its own separation
              marginTop: "16px",
              width: "100%",
              alignItems: "flex-end"
            }}
            form={form}
            name="subscription_filter_form"
            onFinish={handleSubmitFilters}
            /**
             * Handles form value changes to synchronize filter dependencies.
             *
             * 1. If 'isPaymentLinked' is set, it automatically sets the 'type' to 'PURCHASE'
             *    since payment status is only applicable to purchase-type subscriptions.
             * 2. If the 'type' is changed away from 'PURCHASE', it clears the 'isPaymentLinked'
             *    filter to avoid invalid state combinations.
             *
             * @param {Object} changed - The fields that have changed.
             * @param {Object} all - All current form field values.
             */
            onValuesChange={(changed, all) => {
              const { isPaymentLinked, type } = changed;

              if (isPaymentLinked !== undefined) {
                form.setFieldsValue({ type: "PURCHASE" });
              }

              if (type && type !== "PURCHASE" && all.isPaymentLinked !== undefined) {
                form.setFieldsValue({ isPaymentLinked: undefined });
              }
            }}
          >
            <Form.Item className="type-drop-down" name="type" label="Type">
              <Select
                style={{ width: "min(150px, 100%)" }}
                options={subscriptionTypesList}
                showSearch
                placeholder="Filter type"
                optionFilterProp="children"
                filterOption={(input, option) => (option?.label ?? "").includes(input)}
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item className="admin-drop-down" name="adminId" label="Admin">
              <Select
                style={{ width: "min(300px, 100%)" }}
                options={adminsNamesList}
                showSearch
                placeholder="Filter Admin for internal type only"
                optionFilterProp="children"
                filterOption={(input, option) => (option?.label ?? "").includes(input)}
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="isPaymentLinked" label="Payment Linked">
              <Select
                style={{ width: "min(150px, 100%)" }}
                options={[
                  { label: "Linked", value: true },
                  { label: "Not Linked", value: false }
                ]}
                placeholder="Payment Linked"
                allowClear
              />
            </Form.Item>
            <Form.Item className="submit-btn">
              <Button type="primary" htmlType="submit">
                Filter
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
      <br />
      <Table
        columns={columns}
        dataSource={subscribersList || []}
        bordered
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      <br />
      <Pagination
        total={totalSubscriptions}
        showSizeChanger={false}
        showQuickJumper={false}
        pageSize={10}
        current={currentPage}
        onChange={handlePageChange}
      />
      {isNewSubscriptionModalOpen === true && (
        <CreateSubscriptionModal
          isModalOpen={isNewSubscriptionModalOpen}
          setIsModalOpen={setIsNewSubscriptionModalOpen}
          handleCreate={handleSubscriptionCreate}
        />
      )}
      {isViewProgressModalOpen === true && (
        <UserProgressModal
          isModalOpen={isViewProgressModalOpen}
          setIsModalOpen={setIsViewProgressModalOpen}
          courseId={courseId}
          userId={userProgressId}
          ROOT_URL={ROOT_URL}
        />
      )}
      {isEditSubscriptionConfigModalOpen === true && (
        <EditSubscriptionConfigModal
          isModalOpen={isEditSubscriptionConfigModalOpen}
          setIsModalOpen={setIsEditSubscriptionConfigModalOpen}
          initialValues={currentSubscriptionRow.config || {}}
          handleUpdateSubscriptionConfig={handleUpdateSubscriptionConfig}
        />
      )}
      {isLinkPaymentModalOpen === true && (
        <LinkPaymentModal
          isModalOpen={isLinkPaymentModalOpen}
          setIsModalOpen={setIsLinkPaymentModalOpen}
          handleLinkPayment={handleLinkPayment}
        />
      )}
      {isDeleteModalOpen && deletingSubscription && (
        <Modal
          title="Delete Subscription"
          open={isDeleteModalOpen}
          onCancel={() => {
            setIsDeleteModalOpen(false);
            setDeletingSubscription(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeletingSubscription(null);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="delete"
              type="primary"
              onClick={() => {
                handleSubscriptionDeletion(deletingSubscription, false);
                setIsDeleteModalOpen(false);
                setDeletingSubscription(null);
              }}
            >
              Just Delete
            </Button>,
            <Button
              key="refund"
              type="primary"
              danger
              onClick={() => {
                handleSubscriptionDeletion(deletingSubscription, true);
                setIsDeleteModalOpen(false);
                setDeletingSubscription(null);
              }}
            >
              Refund & Delete
            </Button>
          ]}
        >
          <p>
            This is a purchase subscription with a linked payment. Would you also like to mark the
            payment as refunded?
          </p>
        </Modal>
      )}
    </Modal>
  );
};
export default CourseUsersModal;
